import type {
	FilterChangedEvent,
	GetRowIdParams,
	PaginationChangedEvent,
	SortChangedEvent,
} from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import type { AxiosError } from "axios";
import axios from "axios";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchHistoryApi } from "@/api/SearchHistoryApi";
import { ErrorAlert, ExportButton } from "@/components";
import ColumnCustomizeFilter from "@/components/ColumnCustomizeFilter";
import DefaultGrid from "@/components/Common/DefaultGrid/DefaultGrid";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_SELECTOR, LOADING_TEXT } from "@/constants";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useSearchOnEnter } from "@/hooks/useSearchOnEnter";
import type QueryParams from "@/interfaces/QueryParams";
import type SearchGridSchema from "@/interfaces/SearchGridSchema";
import type { GridRow, HistoricalMapRow, LatestMapRow } from "@/interfaces/SearchGridSchema";
import APIErrorHandling from "@/pages/error/ApiErrorHandlingPage";
import LoadingPage from "@/pages/LoadingPage";
import { getMapKey, SupplyChainColumnDefinitions } from "@/pages/search/Details/ColumnDefinitions";
import SearchForm from "@/pages/search/Details/Form";
import SearchPaginationPanel from "@/pages/search/Details/SearchPaginationPanel";
import { useFetchSearchResults } from "../../hooks/useFetchSearchResults";
import Logger from "../../utils/Logger";

const DEFAULT_SEARCH_PARAMS: QueryParams = {
	commodity: [],
	country: [],
	itemDescription: "",
	itemNumber: undefined,
	vendorName: "",
	vendorNumber: undefined,
};

type SearchSortRule = {
	colId: string;
	sort: "asc" | "desc";
};

// Map API rows directly to grid rows preserving original order.
// `historyIndex` (parentKey + array index) provides a stable AG Grid row ID only.
// Parent risk statuses are carried from the latest row so historical score pills match it.
const buildHistoricalRows = (
	rows: SearchGridSchema[],
	parentKey: string,
	parent: LatestMapRow,
): HistoricalMapRow[] => {
	return rows.map((row, index) => ({
		rowType: "history",
		parentKey,
		historyIndex: index,
		parentScoreRiskStatus: parent.scoreRiskStatus,
		parentTransparencyRiskStatus: parent.transparencyRiskStatus,
		parentEnvironmentalRiskStatus: parent.environmentalRiskStatus,
		parentHumanRightsRiskStatus: parent.humanRightsRiskStatus,
		fiscalYear: row.fiscalYear ?? null,
		mapVersion: row.mapVersion ?? null,
		scoreDate: row.scoreDate ?? null,
		transparencyRiskScore: row.transparencyRiskScore ?? null,
		overallRiskScore: row.overallRiskScore ?? null,
		environmentalRiskScore: row.environmentalRiskScore ?? null,
		humanRightsRiskScore: row.humanRightsRiskScore ?? null,
	}));
};

const SearchPage: React.FC = () => {
	usePageTracking("Search Page");

	const [isSearchEnabled, setIsSearchEnabled] = useState(false);
	const [doSearchFn, setDoSearchFn] = useState<(() => void) | null>(null);
	const [searchParams, setSearchParams] = useState<QueryParams>(DEFAULT_SEARCH_PARAMS);
	const [submittedSearchParams, setSubmittedSearchParams] =
		useState<QueryParams>(DEFAULT_SEARCH_PARAMS);
	const [isOpen, setIsOpen] = useState(false);
	const [exportErrorStatus, setExportErrorStatus] = useState<
		AxiosError | { message?: string } | string
	>("");
	const [exportErrorType, setExportErrorType] = useState("");

	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
	const [historyByMap, setHistoryByMap] = useState<Record<string, HistoricalMapRow[]>>({});
	const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

	const gridRef = useRef<AgGridReact<GridRow> | null>(null);
	const historyRequestVersionRef = useRef(0);
	const historyRequestControllersRef = useRef<Map<string, AbortController>>(new Map());
	const expandedKeysRef = useRef(expandedKeys);
	const historyByMapRef = useRef(historyByMap);
	const loadingKeysRef = useRef(loadingKeys);
	const [searchTrigger, setSearchTrigger] = useState(0);
	const [sortModel, setSortModel] = useState<SearchSortRule[]>([]);
	const [filterModel, setFilterModel] = useState<Record<string, unknown>>({});

	const {
		latestRows,
		totalCount: totalLatestRows,
		loading,
		noResultsFound,
		inlineError,
		clearInlineError,
	} = useFetchSearchResults({
		searchParams: submittedSearchParams,
		searchTrigger,
		currentPage,
		pageSize,
		sortModel,
		filterModel,
	});

	// Column filters applied directly in AG Grid (as opposed to the search form) re-run the
	// server search but should keep the grid + headers visible, showing AG Grid's own
	// "No Record(s) Found" overlay instead of the full-page "No Results Found" message.
	const hasActiveColumnFilters = Object.keys(filterModel).length > 0;
	const showGrid =
		searchTrigger > 0 &&
		!inlineError &&
		(loading || latestRows.length > 0 || (noResultsFound && hasActiveColumnFilters));

	// Reset all expansion state and cancel any in-flight history requests.
	// This increments historyRequestVersionRef to make in-flight results no-op when they return,
	// aborts controllers to cancel network requests, and clears stored history/expanded sets.
	const resetExpansionState = useCallback(() => {
		historyRequestVersionRef.current += 1;
		for (const controller of historyRequestControllersRef.current.values()) {
			controller.abort();
		}
		historyRequestControllersRef.current.clear();
		setExpandedKeys(new Set());
		setHistoryByMap({});
		setLoadingKeys(new Set());
	}, []);

	const getRowId = useCallback((params: GetRowIdParams<GridRow>) => {
		const row = params.data;
		// Check the "history" discriminant first so TS can narrow the fall-through
		// (else) branch to LatestMapRow.
		if (row.rowType === "history") {
			return `history:${row.parentKey}::${row.historyIndex}`;
		}
		return `latest:${row.scmIDNum}::${row.itemNumber}`;
	}, []);

	useEffect(() => {
		expandedKeysRef.current = expandedKeys;
	}, [expandedKeys]);

	useEffect(() => {
		historyByMapRef.current = historyByMap;
	}, [historyByMap]);

	useEffect(() => {
		loadingKeysRef.current = loadingKeys;
	}, [loadingKeys]);

	useEffect(() => {
		return () => {
			for (const controller of historyRequestControllersRef.current.values()) {
				controller.abort();
			}
			historyRequestControllersRef.current.clear();
		};
	}, []);

	// Fetch and store history rows for a single key. Extracted from handleToggleHistory
	// to keep cognitive complexity of each function under the project's lint threshold.
	const loadHistoryForKey = useCallback(async (row: LatestMapRow, key: string) => {
		// Remember the current version so we can ignore stale responses after a reset.
		const requestVersion = historyRequestVersionRef.current;
		const controller = new AbortController();
		historyRequestControllersRef.current.set(key, controller);
		setExpandedKeys((prev) => new Set(prev).add(key));
		setLoadingKeys((prev) => new Set(prev).add(key));

		Logger.info(
			`Loading history for row ${key} (SCM ID: ${row.scmIDNum}, Item Number: ${row.itemNumber})`,
			["SearchHistoryApi"],
		);

		try {
			const historyRows = await SearchHistoryApi.getSearchHistory(
				Number(row.scmIDNum),
				Number(row.itemNumber),
				{ signal: controller.signal },
			);

			// If a resetExpansionState ran while this request was inflight, its version will differ and we ignore the result.
			if (historyRequestVersionRef.current !== requestVersion) {
				return;
			}

			// Persist fetched history (will cause displayRows to include these rows under the latest row)
			setHistoryByMap((prev) => ({
				...prev,
				[key]: buildHistoricalRows(historyRows, key, row),
			}));
		} catch (error) {
			// Cancellation is expected during rapid interactions or when resetting; do not treat as an error.
			if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
				return;
			}
			Logger.error(`Failed to fetch history for key: ${key}`, error, ["SearchPage"]);
			// If still on the same requestVersion, collapse the row to reflect failure.
			if (historyRequestVersionRef.current === requestVersion) {
				setExpandedKeys((prev) => {
					const next = new Set(prev);
					next.delete(key);
					return next;
				});
			}
		} finally {
			historyRequestControllersRef.current.delete(key);
			setLoadingKeys((prev) => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		}
	}, []);

	// Toggle history rows for a given latest row.
	// Behavior summary:
	// 1) Validate identifiers — if missing, do nothing.
	// 2) If already expanded: collapse by removing key from expandedKeys.
	// 3) If history already loaded: just expand (no network call).
	// 4) Otherwise: delegate the fetch/store/error-handling to loadHistoryForKey.
	const handleToggleHistory = useCallback(
		async (row: LatestMapRow) => {
			// Basic validation: we need both identifiers to fetch history.
			if (row.scmIDNum == null || row.itemNumber == null) {
				Logger.warn("Unable to load map history because the row is missing identifiers", [
					"SearchPage",
				]);
				return;
			}

			const key = getMapKey(row);

			// If expanded already -> collapse
			if (expandedKeysRef.current.has(key)) {
				setExpandedKeys((prev) => {
					const next = new Set(prev);
					next.delete(key);
					return next;
				});
				return;
			}

			// If we've already fetched history for this key -> just expand (no fetch)
			if (historyByMapRef.current[key]) {
				setExpandedKeys((prev) => new Set(prev).add(key));
				return;
			}

			// If a fetch is already in progress for this key, ignore duplicate clicks
			if (loadingKeysRef.current.has(key)) {
				return;
			}

			await loadHistoryForKey(row, key);
		},
		[loadHistoryForKey],
	);

	const historyGridContext = useMemo(
		() => ({
			expandedKeys,
			loadingKeys,
			onToggleHistory: handleToggleHistory,
		}),
		[expandedKeys, loadingKeys, handleToggleHistory],
	);

	// Build the flattened row list for the client-side grid. For each "latest" row we either
	// return only the latest row, or the latest row followed by its history rows (if expanded).
	// NOTE: history rows are injected client-side for display only and do NOT consume server page slots.
	const displayRows = useMemo<GridRow[]>(() => {
		return latestRows.flatMap((latestRow, idx) => {
			const key = getMapKey(latestRow);
			const taggedLatest: LatestMapRow & { __parentIndex?: number } = {
				...latestRow,
				rowType: "latest",
				__parentIndex: idx,
			};

			if (!expandedKeys.has(key)) {
				return [taggedLatest];
			}

			// History rows carry only their own historical fields — never the parent's values.
			// __parentIndex is UI-only bookkeeping so the group-aware comparator keeps them
			// directly under their parent row.
			const historyRows = (historyByMap[key] ?? []).map((h) => ({
				...h,
				__parentIndex: idx,
			}));

			return [taggedLatest, ...historyRows];
		});
	}, [latestRows, expandedKeys, historyByMap]);

	const handlePageChange = useCallback(
		(newPage: number) => {
			if (newPage === currentPage) {
				return;
			}
			resetExpansionState();
			setCurrentPage(newPage);
			gridRef.current?.api.ensureIndexVisible(0, "top");
		},
		[currentPage, resetExpansionState],
	);

	// Page size changed via the pagination panel's selector: reset paging/history and
	// reload from page 0, since the previous page's row-range no longer applies once
	// the number of rows per page changes.
	const handlePageSizeChange = useCallback(
		(newPageSize: number) => {
			if (newPageSize === pageSize) {
				return;
			}
			resetExpansionState();
			setPageSize(newPageSize);
			setCurrentPage(0);
			gridRef.current?.api.ensureIndexVisible(0, "top");
		},
		[pageSize, resetExpansionState],
	);

	const totalPages = Math.max(1, Math.ceil(totalLatestRows / pageSize));
	const visibleLatestStart = totalLatestRows === 0 ? 0 : currentPage * pageSize + 1;
	const visibleLatestEnd =
		totalLatestRows === 0 ? 0 : currentPage * pageSize + Math.min(latestRows.length, pageSize);

	// Keep this callback to stay composition-close to dev SearchPage.
	// We still use client-side latest-row pagination, so this handler is mainly for visual parity/readability.
	const handlePaginationPageSizeChanged = useCallback((event: PaginationChangedEvent) => {
		const api = event.api;
		setTimeout(() => {
			if (api) {
				api.refreshCells();
				api.redrawRows();
			}
		}, 0);
	}, []);

	const renderPaginationFooter = () => {
		if (totalLatestRows === 0) {
			return null;
		}

		// Custom panel mirrors the native AG Grid paging panel's look (|< < > >| glyphs,
		// "Page n of nn" and row summary grouped on the left, Page Size docked right),
		// while paging/page-size logic remains custom so history rows never consume
		// latest-row page slots. See SearchPaginationPanel for the presentational markup.
		return (
			<SearchPaginationPanel
				currentPage={currentPage}
				totalPages={totalPages}
				pageSize={pageSize}
				pageSizeOptions={DEFAULT_PAGE_SIZE_SELECTOR}
				visibleStart={visibleLatestStart}
				visibleEnd={visibleLatestEnd}
				totalRows={totalLatestRows}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
			/>
		);
	};

	// handleSearchClick accepts an optional `overrideParams` argument.
	// SearchForm composes the freshly-typed params locally and calls
	// handleSearchClick(newSearchParams) directly, in the SAME click/keystroke
	// that also calls updateSearchParams(newSearchParams). Because React state
	// updates are asynchronous, `searchParams` here can still hold the PREVIOUS
	// value at the moment this callback runs. Reading only `searchParams` was
	// the root cause of the "one search click behind" bug: click #1 fired the
	// request with click #0's params, and only click #2 picked up click #1's
	// params. Using overrideParams (when provided) guarantees we submit the
	// exact params the user just entered, on the very first click/Enter.
	const handleSearchClick = useCallback(
		(overrideParams?: Partial<QueryParams>) => {
			resetExpansionState();
			setCurrentPage(0);
			setSortModel([]);
			setFilterModel({});
			setSubmittedSearchParams((overrideParams as QueryParams) ?? searchParams);
			setSearchTrigger((prev) => prev + 1);
		},
		[resetExpansionState, searchParams],
	);

	// When user changes column sort in the UI, capture AG Grid's column state and
	// convert to the internal sortModel shape. Important: sorting operates on the
	// latest dataset only (server-side), so we reset expansion state and move to page 0
	// to fetch the newly-sorted first page.
	const handleSortChanged = useCallback(
		(event: SortChangedEvent<GridRow>) => {
			if (searchTrigger === 0) {
				return;
			}

			const nextSortModel = event.api
				.getColumnState()
				.filter((state) => state.sort === "asc" || state.sort === "desc")
				.map((state) => ({
					colId: state.colId,
					sort: state.sort as "asc" | "desc",
				}));

			setSortModel((prevSortModel) => {
				if (JSON.stringify(prevSortModel) === JSON.stringify(nextSortModel)) {
					return prevSortModel;
				}

				// Sorting changed the dataset/page scope: reset paging/history and reload from page 0.
				resetExpansionState();
				setCurrentPage(0);
				return nextSortModel;
			});
		},
		[resetExpansionState, searchTrigger],
	);

	// Similar to sorting: when filters change we translate AG Grid's filterModel
	// to the internal filterModel and reset expansion/pagination so server returns
	// the first page of filtered latest rows.
	const handleFilterChanged = useCallback(
		(event: FilterChangedEvent<GridRow>) => {
			if (searchTrigger === 0) {
				return;
			}

			const nextFilterModel = event.api.getFilterModel() as Record<string, unknown>;
			setFilterModel((prevFilterModel) => {
				if (JSON.stringify(prevFilterModel) === JSON.stringify(nextFilterModel)) {
					return prevFilterModel;
				}

				// Filtering changed latest dataset/page scope: reset paging/history and reload from page 0.
				resetExpansionState();
				setCurrentPage(0);
				return nextFilterModel;
			});
		},
		[resetExpansionState, searchTrigger],
	);

	const handleDoSearchChange = useCallback((fn: () => void) => {
		setDoSearchFn(() => fn);
	}, []);

	// Hook 1: Listen for Enter keypress
	useSearchOnEnter({
		isEnabled: isSearchEnabled,
		onSearch: () => doSearchFn?.(),
		searchParams,
	});

	const renderAgGridTableWithCustomizeColumnView = () => {
		return (
			<div className="w-full">
				{showGrid && (
					<div>
						<div id="grid-control" className="section-header">
							<div id="column-customize-view-button" className="flex-item pr-8">
								<button
									type="button"
									className="flex flex-row place-content-end"
									onClick={() => setIsOpen(!isOpen)}
								>
									<div className="section-header__badge">
										<div className="section-header__icon icon-filter_list" />
									</div>
									<p className="inline-block text-blue-600 px-4">Customize Column View</p>
								</button>
							</div>

							{totalLatestRows !== 0 && (
								<div id="export-results-button" className="flex-item pr-8">
									<ExportButton
										mode="filtered"
										searchParams={submittedSearchParams}
										onExportError={(error) => {
											setExportErrorType("exportCSV");
											setExportErrorStatus(error as AxiosError);
										}}
									/>
								</div>
							)}
						</div>
						<div className="pl-4 items-center justify-start">
							{isOpen && gridRef.current && (
								<ColumnCustomizeFilter
									gridApi={gridRef.current.api as never}
									onClickOutside={() => setIsOpen(false)}
								/>
							)}
						</div>

						{/* Grid + pagination panel share one wrapper so the panel reads as a
						    continuation of the grid (matching AG Grid's native auto-styled
						    footer), with a single light shadow cast below the whole block. */}
						<div className="w-full shadow-md">
							<DefaultGrid
								columnDefs={SupplyChainColumnDefinitions as never}
								rowModelType="clientSide"
								rowData={displayRows as never}
								loading={loading}
								onRowClicked={() => undefined}
								onSortChanged={handleSortChanged}
								onFilterChanged={handleFilterChanged}
								onPaginationPageSizeChanged={handlePaginationPageSizeChanged}
								rowId={getRowId as never}
								noRowsFoundText="No Record(s) Found"
								quickFilterText={submittedSearchParams.itemDescription}
								context={historyGridContext}
								pagination={false}
								ref={gridRef as never}
							/>
							{renderPaginationFooter()}
						</div>
						<div className="h-10" />
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="w-full h-full flex flex-col">
			<div className="pb-2 mt-20 w-full flex justify-center 2xl:pt-6">
				<div className="w-full px-5">
					{inlineError && (
						<ErrorAlert message={inlineError} onDismiss={clearInlineError} className="mx-5 mb-4" />
					)}
					<SearchForm
						searchParams={searchParams}
						updateSearchParams={setSearchParams}
						handleSearchClick={handleSearchClick}
						searchCardExpanded={setIsSearchEnabled}
						onDoSearchChange={handleDoSearchChange}
						shouldKeepExpanded={(noResultsFound && !hasActiveColumnFilters) || Boolean(inlineError)}
					/>
				</div>
			</div>
			<div className="w-full px-5">{renderAgGridTableWithCustomizeColumnView()}</div>
			{noResultsFound && !hasActiveColumnFilters && (
				<div className="flex flex-col items-center justify-center py-20 text-center">
					<div className="icon-search text-5xl text-blue-600 mb-4" />
					<h2 className="text-xl font-semibold text-blue-600 mb-2">No Results Found</h2>
					<p className="text-black-600">Try adjusting your search criteria.</p>
				</div>
			)}
			<div id="error-handling" className="w-full px-8">
				{!loading && exportErrorStatus && (
					<APIErrorHandling error={exportErrorStatus} type={exportErrorType} />
				)}
			</div>
		</div>
	);
};

export default SearchPage;
